import { useFeaturedImage } from '@/context/FeaturedImagesContext';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { resolveImageUrl } from '@/context/FeaturedImagesContext';
import { cn } from '@/lib/utils';

/**
 * FeaturedImage — renders an admin-curated image for a given slot.
 * - If admin has assigned a gallery item to the slot: renders <img>.
 * - Otherwise: renders an elegant CSS gradient fallback using brand tokens
 *   (cream → sage). Never references third-party stock photos.
 *
 * Props:
 *   slot:            required — one of FEATURED_SLOTS keys.
 *   alt:             accessible alt text (used for the <img>; fallback is decorative).
 *   className:       wrapper classes (aspect/rounded/shadow). Wrapper is positioned
 *                    relative so children (img/fallback) fill it.
 *   imgClassName:    classes applied to the <img> (e.g. object-cover).
 *   priority:        if true, uses eager loading + fetchpriority high.
 *   overlay:         optional ReactNode rendered above the image (e.g. gradient veil).
 *   children:        optional ReactNode rendered above overlay (e.g. text content).
 *   testId:          optional data-testid.
 *   fallbackLabel:   short text shown faintly in the fallback (decorative).
 *   fallbackVariant: 'tile' (default) shows the elegant decorative label;
 *                    'hero' shows only the gradient (no label) so it doesn't
 *                    compete visually with text content placed on top.
 */
export const FeaturedImage = ({
    slot,
    alt = '',
    className = '',
    imgClassName = 'w-full h-full object-cover',
    priority = false,
    overlay = null,
    children = null,
    testId,
    fallbackLabel = 'Floreal',
    fallbackVariant = 'tile',
}) => {
    const { hasImage, imageUrl } = useFeaturedImage(slot);

    return (
        <div
            data-testid={testId}
            className={cn('relative overflow-hidden bg-muted', className)}
        >
            {hasImage ? (
                <img
                    src={imageUrl}
                    alt={alt}
                    loading={priority ? 'eager' : 'lazy'}
                    {...(priority ? { fetchpriority: 'high' } : {})}
                    className={imgClassName}
                />
            ) : (
                <FeaturedFallback label={fallbackLabel} variant={fallbackVariant} />
            )}
            {overlay}
            {children}
        </div>
    );
};

// Pure CSS fallback with brand tokens (cream → sage). Variants:
//  - 'tile' (default): includes a decorative "Atelier / Floreal" label.
//  - 'hero': pure gradient + grain (no label) so it composes well as a
//            background for big hero text.
export const FeaturedFallback = ({ label = 'Floreal', className = '', variant = 'tile' }) => {
    return (
        <div
            aria-hidden="true"
            className={cn(
                'absolute inset-0 w-full h-full bg-gradient-to-br from-atelier-cream via-atelier-beige to-atelier-sage/55',
                className,
            )}
        >
            <div className="absolute inset-0 grain opacity-70" />
            {variant === 'tile' && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center px-6">
                        <p className="eyebrow text-atelier-bronze-soft tracking-[0.32em]">Atelier</p>
                        <p className="font-serif italic text-3xl sm:text-4xl text-atelier-bronze/85 mt-3 leading-none">
                            {label}
                        </p>
                        <span className="mt-4 inline-block h-px w-12 bg-atelier-bronze-soft/70" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeaturedImage;

/**
 * CategoryImage — picks the most recent gallery item for a category and
 * displays it. Useful for service cards or auxiliary thumbnails when there's
 * no dedicated featured slot. Falls back to the same brand gradient.
 *
 * Props mirror FeaturedImage but use `category` instead of `slot`.
 */
export const CategoryImage = ({
    category,
    alt = '',
    className = '',
    imgClassName = 'w-full h-full object-cover',
    fallbackLabel = 'Floreal',
}) => {
    const [item, setItem] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/gallery', {
                    params: category ? { category } : {},
                });
                if (!cancelled) setItem((res.data || [])[0] || null);
            } catch {
                if (!cancelled) setItem(null);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [category]);

    return (
        <div className={cn('relative overflow-hidden bg-muted', className)}>
            {item ? (
                <img
                    src={resolveImageUrl(item.image_url)}
                    alt={alt || item.title || fallbackLabel}
                    loading="lazy"
                    className={imgClassName}
                />
            ) : loaded ? (
                <FeaturedFallback label={fallbackLabel} />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-atelier-cream via-atelier-beige to-atelier-sage/30 animate-pulse" />
            )}
        </div>
    );
};
